import React, { createContext, useState, useEffect } from 'react';

import { getDefaultContact, runValidation } from '../utils';
import { ContactStateType, ContactType, ContactErrors } from '../types';

// Context
type StateUpdateType =
  | Partial<ContactStateType>
  | ((state: ContactStateType) => ContactStateType);

const ContactStateContext =
  createContext<
    [
      ContactStateType,
      (update: StateUpdateType) => void,
      ContactErrors,
      boolean,
      boolean,
    ]
  >(null);

export default ContactStateContext;

type ContactModalContextProviderProps = {
  contact?: Partial<ContactStateType>;
  isProfile: boolean;
  addBuyerContact: boolean;
  onContactChange: (c: ContactStateType, allInputValid: boolean) => void;
  feedbackSendToSeller?: boolean;
};

export const ContactStateContextProvider: React.FC<
  ContactModalContextProviderProps
> = ({
  contact,
  onContactChange,
  isProfile,
  addBuyerContact = false,
  children,
  feedbackSendToSeller,
}) => {
  const getContactType = () => {
    let contactType: ContactType = 'Seller';
    if (addBuyerContact) {
      contactType = 'Buyer';
    } else if (isProfile) {
      contactType = 'Agent';
    }
    return contactType;
  };

  const [contactState, setContactState] = useState<ContactStateType>(() => ({
      ...getDefaultContact(getContactType(), feedbackSendToSeller),
      ...contact,
    }));
  const [errors, isAllInputValid] = runValidation(contactState);

  useEffect(() => {
    onContactChange(contactState, isAllInputValid);
  }, [contactState, onContactChange, isAllInputValid]);

  useEffect(() => {
    const contactToUpdate = { ...contact };
    if (contact?.type === 'Agent') {
      delete contactToUpdate.phone;
    }

    setContactState({
      ...getDefaultContact(getContactType(), feedbackSendToSeller),
      ...contactToUpdate,
    });
  }, [contact, isProfile]);

  const updateContactState = (update: StateUpdateType) => {
    if (typeof update === 'function') {
      return setContactState(update);
    }

    setContactState(prevState => ({
      ...prevState,
      ...update,
    }));
  };

  return (
    <ContactStateContext.Provider
      value={[
        contactState,
        updateContactState,
        errors,
        isAllInputValid,
        feedbackSendToSeller,
      ]}
    >
      {children}
    </ContactStateContext.Provider>
  );
};
